export type ShiftReportFieldInputType = 'text' | 'staff' | 'staffMulti';

export interface ShiftReportField {
  id: string;
  label: string;
  placeholder?: string;
  enabled: boolean;
  order: number;
  inputType?: ShiftReportFieldInputType;
}

export interface ShiftReportSection {
  id: string;
  title: string;
  order: number;
  enabled?: boolean;
  fields: ShiftReportField[];
}

export type ShiftReportTemplate = ShiftReportSection[];
export type ShiftReportValues = Record<string, string>;

export const DEFAULT_SHIFT_REPORT_TEMPLATE: ShiftReportTemplate = [
  {
    id: 'staff',
    title: 'Staff on Duty',
    order: 1,
    fields: [
      { id: 'supervisor', label: 'Shift Supervisor / Lead', placeholder: 'Select staff', enabled: true, order: 1, inputType: 'staff' },
      { id: 'rasOnDuty', label: 'RAs on Duty', placeholder: 'Select staff', enabled: true, order: 2, inputType: 'staffMulti' },
      { id: 'guardsOnDuty', label: 'Guards on Duty', placeholder: 'Select staff', enabled: true, order: 3, inputType: 'staffMulti' },
      { id: 'callOuts', label: 'Staff Call-Outs', placeholder: 'Select staff', enabled: true, order: 4, inputType: 'staffMulti' },
    ],
  },
  {
    id: 'accountability',
    title: 'Accountability / Totals',
    order: 2,
    fields: [
      { id: 'clientCount', label: 'Client Count', enabled: true, order: 1 },
      { id: 'bedsAvailable', label: 'Beds Available', enabled: true, order: 2 },
      { id: 'offlineBeds', label: 'Offline Beds', enabled: true, order: 3 },
      { id: 'reservedBeds', label: 'Reserved Beds', enabled: true, order: 4 },
      { id: 'latePasses', label: 'Late Passes', enabled: true, order: 5 },
      { id: 'overnightPasses', label: 'Overnight Passes', enabled: true, order: 6 },
      { id: 'multiUsePasses', label: 'Multi-Use Passes', enabled: true, order: 7 },
      { id: 'hospital', label: 'Hospital', enabled: true, order: 8 },
      { id: 'returnees', label: 'Returnee(s)', enabled: true, order: 9 },
      { id: 'exitUnknown', label: 'Exit Unknown', enabled: true, order: 10 },
      { id: 'curfewViolators', label: 'Curfew Violators', enabled: true, order: 11 },
      { id: 'intakes', label: 'Intake(s)', enabled: true, order: 12 },
      { id: 'unbedded', label: 'Unbedded', enabled: true, order: 13 },
      { id: 'overage', label: 'Overage', enabled: true, order: 14 },
    ],
  },
  {
    id: 'equipment',
    title: 'Equipment / Inventory',
    order: 3,
    fields: [
      { id: 'masterKeys', label: 'Master Keys', enabled: true, order: 1 },
      { id: 'iphone', label: 'iPhone', enabled: true, order: 2 },
      { id: 'iphoneCharger', label: 'iPhone Charger', enabled: true, order: 3 },
      { id: 'radios', label: 'Radios', enabled: true, order: 4 },
      { id: 'wands', label: 'Wands', enabled: true, order: 5 },
      { id: 'laundryCards', label: 'Laundry Cards Returned', enabled: true, order: 6 },
      { id: 'mealsDistributed', label: 'Meals Distributed', enabled: true, order: 7 },
      { id: 'juiceDistributed', label: 'Juice Distributed', enabled: true, order: 8 },
      { id: 'waterBottles', label: 'Water Bottles', enabled: true, order: 9 },
      { id: 'clearBags', label: 'Clear Bags Used', enabled: true, order: 10 },
      { id: 'blackBags', label: 'Black Bags Used', enabled: true, order: 11 },
      { id: 'careKits', label: 'Care Kits', enabled: true, order: 12 },
      { id: 'metrocards', label: 'Metrocards', enabled: true, order: 13 },
      { id: 'incidents', label: 'Incidents', enabled: true, order: 14 },
      { id: 'nsrSignatures', label: 'NSR Signatures', enabled: true, order: 15 },
    ],
  },
  {
    id: 'meals',
    title: 'Meals & Beverages',
    order: 4,
    fields: [
      { id: 'breakfastReceived', label: 'Breakfast Received', enabled: true, order: 1 },
      { id: 'breakfastDistributed', label: 'Breakfast Distributed', enabled: true, order: 2 },
      { id: 'breakfastDiscarded', label: 'Breakfast Discarded', enabled: true, order: 3 },
      { id: 'breakfastMenu', label: 'Breakfast Menu', enabled: true, order: 4 },
      { id: 'lunchReceived', label: 'Lunch Received', enabled: true, order: 5 },
      { id: 'lunchDistributed', label: 'Lunch Distributed', enabled: true, order: 6 },
      { id: 'lunchDiscarded', label: 'Lunch Discarded', enabled: true, order: 7 },
      { id: 'lunchMenu', label: 'Lunch Menu', enabled: true, order: 8 },
      { id: 'dinnerReceived', label: 'Dinner Received', enabled: true, order: 9 },
      { id: 'dinnerDistributed', label: 'Dinner Distributed', enabled: true, order: 10 },
      { id: 'dinnerDiscarded', label: 'Dinner Discarded', enabled: true, order: 11 },
      { id: 'dinnerMenu', label: 'Dinner Menu', enabled: true, order: 12 },
      { id: 'milkReceived', label: 'Milk Received', enabled: true, order: 13 },
      { id: 'milkDistributed', label: 'Milk Distributed', enabled: true, order: 14 },
      { id: 'milkDiscarded', label: 'Milk Discarded', enabled: true, order: 15 },
      { id: 'milkBrand', label: 'Milk Brand/Notes', enabled: true, order: 16 },
      { id: 'juiceReceived', label: 'Juice Received', enabled: true, order: 17 },
      { id: 'juiceDistributed', label: 'Juice Distributed', enabled: true, order: 18 },
      { id: 'juiceDiscarded', label: 'Juice Discarded', enabled: true, order: 19 },
      { id: 'juiceBrand', label: 'Juice Brand/Notes', enabled: true, order: 20 },
    ],
  },
];

const sectionTitles: Record<string, string> = {
  staff: 'Staff on Duty',
  staffFields: 'Staff on Duty',
  accountability: 'Accountability / Totals',
  accountabilityFields: 'Accountability / Totals',
  equipment: 'Equipment / Inventory',
  equipmentFields: 'Equipment / Inventory',
  meals: 'Meals & Beverages',
};

const cloneDefault = (): ShiftReportTemplate => DEFAULT_SHIFT_REPORT_TEMPLATE.map((section) => ({
  ...section,
  fields: section.fields.map((field) => ({ ...field })),
}));

const slugify = (value: string, fallback: string) => {
  const slug = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return slug || fallback;
};

const normalizeField = (raw: unknown, index: number): ShiftReportField | null => {
  const row = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const label = typeof row.label === 'string' ? row.label.trim() : '';
  if (!label) return null;
  const id = typeof row.id === 'string' && row.id.trim()
    ? row.id.trim()
    : slugify(label, `field-${index + 1}`);
  return {
    id,
    label,
    placeholder: typeof row.placeholder === 'string' ? row.placeholder : '',
    enabled: typeof row.enabled === 'boolean' ? row.enabled : true,
    order: Number.isFinite(row.order) ? Number(row.order) : index + 1,
    inputType: row.inputType === 'staff' || row.inputType === 'staffMulti' ? row.inputType : 'text',
  };
};

const normalizeSection = (raw: unknown, index: number, fallbackId?: string): ShiftReportSection | null => {
  const row = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const id = typeof row.id === 'string' && row.id.trim() ? row.id.trim() : fallbackId || `section-${index + 1}`;
  const title = typeof row.title === 'string' && row.title.trim()
    ? row.title.trim()
    : sectionTitles[id] || `Section ${index + 1}`;
  const fields = Array.isArray(row.fields)
    ? row.fields.map(normalizeField).filter((field): field is ShiftReportField => Boolean(field))
    : [];
  return {
    id,
    title,
    order: Number.isFinite(row.order) ? Number(row.order) : index + 1,
    enabled: typeof row.enabled === 'boolean' ? row.enabled : true,
    fields: fields.sort((a, b) => a.order - b.order),
  };
};

const mergeMissingDefaults = (template: ShiftReportTemplate): ShiftReportTemplate => {
  const bySection = new Map(template.map((s) => [s.id, s]));
  const merged: ShiftReportTemplate = template.map((section) => ({ ...section, fields: [...section.fields] }));

  DEFAULT_SHIFT_REPORT_TEMPLATE.forEach((defSection) => {
    const existing = merged.find((s) => s.id === defSection.id);
    if (!existing) {
      merged.push({ ...defSection, fields: defSection.fields.map((f) => ({ ...f })) });
      return;
    }
    const existingIds = new Set(existing.fields.map((f) => f.id));
    defSection.fields.forEach((defField) => {
      if (!existingIds.has(defField.id)) {
        existing.fields.push({ ...defField, order: existing.fields.length + 1 });
      }
    });
  });

  return merged;
};

export const normalizeShiftReportTemplate = (raw: unknown): ShiftReportTemplate => {
  if (!raw) return cloneDefault();

  const input = raw && typeof raw === 'object' ? raw as Record<string, unknown> : null;
  const rawSections = Array.isArray(raw)
    ? raw
    : Array.isArray(input?.sections)
      ? input.sections
      : null;

  if (rawSections) {
    const sections = rawSections
      .map((section, index) => normalizeSection(section, index))
      .filter((section): section is ShiftReportSection => Boolean(section))
      .sort((a, b) => a.order - b.order);
    return sections.length ? sections : cloneDefault();
  }

  if (input) {
    const legacySections = [
      { id: 'staff', fields: input.staffFields },
      { id: 'accountability', fields: input.accountabilityFields },
      { id: 'equipment', fields: input.equipmentFields },
    ].filter((section) => Array.isArray(section.fields));

    const sections = legacySections
      .map((section, index) => normalizeSection({ id: section.id, title: sectionTitles[section.id], fields: section.fields }, index))
      .filter((section): section is ShiftReportSection => Boolean(section));
    if (sections.length) return mergeMissingDefaults(sections);
  }

  return cloneDefault();
};

export const getEnabledShiftReportSections = (template: ShiftReportTemplate): ShiftReportTemplate => (
  normalizeShiftReportTemplate(template)
    .filter((section) => section.enabled !== false)
    .map((section) => ({ ...section, fields: section.fields.filter((field) => field.enabled) }))
    .filter((section) => section.fields.length > 0)
);

export const createEmptyShiftReportValues = (template: ShiftReportTemplate = DEFAULT_SHIFT_REPORT_TEMPLATE): ShiftReportValues => {
  const values: ShiftReportValues = {};
  normalizeShiftReportTemplate(template).forEach((section) => {
    section.fields.forEach((field) => {
      values[field.id] = '';
    });
  });
  return values;
};

export const createShiftReportField = (sectionId: string, index: number): ShiftReportField => ({
  id: `${slugify(sectionId, 'custom')}-${Date.now()}-${index}`,
  label: 'New Field',
  placeholder: '',
  enabled: true,
  order: index + 1,
});

export const validateShiftReportTemplate = (template: ShiftReportTemplate): string | null => {
  const labels = new Set<string>();
  let enabledCount = 0;

  for (const section of template) {
    if (!section.title.trim()) return 'Every section needs a name.';
    for (const field of section.fields) {
      if (!field.label.trim()) return 'Every field needs a label.';
      const key = `${section.id}:${field.label.trim().toLowerCase()}`;
      if (labels.has(key)) return 'Field labels must be unique within each section.';
      labels.add(key);
      if (field.enabled && section.enabled !== false) enabledCount += 1;
    }
  }

  return enabledCount > 0 ? null : 'At least one field must be enabled in an active section.';
};
